/**
 * syncQueue — IndexedDB-backed queue for write requests that fail while offline.
 *
 * The Service Worker handles caching for reads. Writes (POST/PUT/DELETE)
 * pass through to the network; if the network is unreachable, the calling
 * code routes the request here via `enqueue()` and treats it as success
 * (optimistic UI is already in place upstream).
 *
 * On `online` event, `drain()` replays queued requests oldest-first.
 * Server-side idempotency:
 *   - /api/workouts and /api/training-sessions deduplicate on `client_id`
 *   - /api/profile uses `If-Match` optimistic concurrency — a 409 means the
 *     cloud moved ahead; we drop the queued write and let the next debounced
 *     push from ProfileContext send the merged state.
 *
 * Failure model:
 *   - Each item gets up to MAX_ATTEMPTS (3) replays
 *   - After exhaustion the item is marked `failed` and surfaces via the
 *     failure banner (Step 5)
 */

import { openDB } from 'idb';
import axios from 'axios';

const DB_NAME = 'enferno-sync';
const DB_VERSION = 1;
const STORE = 'queue';
const MAX_ATTEMPTS = 3;

let dbPromise;
let draining = false;

const getDB = () => {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          const store = db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
          store.createIndex('by-status', 'status');
          store.createIndex('by-created', 'createdAt');
        }
      },
    });
  }
  return dbPromise;
};

const isNetworkError = (err) => {
  if (!err) return false;
  // axios sets `response` for HTTP errors; absence => transport-level failure
  if (err.response) return false;
  if (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED') return true;
  if (err.message && /network|fetch|failed to fetch|timeout/i.test(err.message)) return true;
  return false;
};

/**
 * enqueue — store a request so it can be replayed later.
 * Returns the assigned queue id.
 */
export const enqueue = async ({ method, url, body, headers = {} }) => {
  const db = await getDB();
  const id = await db.add(STORE, {
    method,
    url,
    body,
    headers,
    status: 'pending',
    attempts: 0,
    createdAt: Date.now(),
    lastError: null,
  });
  return id;
};

export const listAll = async () => {
  const db = await getDB();
  return db.getAll(STORE);
};

export const listPending = async () => {
  const db = await getDB();
  return db.getAllFromIndex(STORE, 'by-status', 'pending');
};

export const listFailed = async () => {
  const db = await getDB();
  return db.getAllFromIndex(STORE, 'by-status', 'failed');
};

export const getCounts = async () => {
  const all = await listAll();
  return {
    pending: all.filter((i) => i.status === 'pending').length,
    failed: all.filter((i) => i.status === 'failed').length,
  };
};

const updateItem = async (id, patch) => {
  const db = await getDB();
  const item = await db.get(STORE, id);
  if (!item) return;
  Object.assign(item, patch);
  await db.put(STORE, item);
};

const removeItem = async (id) => {
  const db = await getDB();
  await db.delete(STORE, id);
};

export const retryFailed = async () => {
  const db = await getDB();
  const failed = await db.getAllFromIndex(STORE, 'by-status', 'failed');
  for (const item of failed) {
    await updateItem(item.id, { status: 'pending', attempts: 0, lastError: null });
  }
  return drain();
};

/**
 * drain — replay all pending items oldest-first. Safe to call repeatedly;
 * concurrent calls are short-circuited by the `draining` guard.
 */
export const drain = async () => {
  if (draining) return;
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;
  draining = true;
  try {
    const items = await listPending();
    items.sort((a, b) => a.createdAt - b.createdAt);
    for (const item of items) {
      try {
        // Tag the payload as offline-queued so the cloud stores the flag and
        // the original (client-side) completion timestamp. Lets the history UI
        // render an [offline] badge and lets analytics distinguish online vs.
        // offline writes. Only mutate POSTs whose body has a `data` envelope
        // (matches /api/workouts and /api/training-sessions shape).
        let body = item.body;
        if ((item.method === 'post' || item.method === 'POST') && body && body.data && typeof body.data === 'object') {
          body = {
            ...body,
            data: {
              ...body.data,
              offline_queued: true,
              original_ts: new Date(item.createdAt).toISOString(),
            },
          };
        }
        await axios({
          method: item.method,
          url: item.url,
          data: body,
          headers: item.headers,
        });
        await removeItem(item.id);
      } catch (err) {
        // 409 on /api/profile means cloud moved on — drop the stale write.
        if (err?.response?.status === 409 && item.url.endsWith('/api/profile')) {
          await removeItem(item.id);
          continue;
        }
        // 4xx (other than 409) means the request itself is bad — don't retry forever.
        if (err?.response && err.response.status >= 400 && err.response.status < 500) {
          await updateItem(item.id, {
            status: 'failed',
            lastError: `HTTP ${err.response.status}: ${err.response.statusText || ''}`,
          });
          continue;
        }
        // Network or 5xx: increment attempts; mark failed at MAX_ATTEMPTS.
        const attempts = (item.attempts || 0) + 1;
        const status = attempts >= MAX_ATTEMPTS ? 'failed' : 'pending';
        await updateItem(item.id, {
          attempts,
          status,
          lastError: err?.message || String(err),
        });
        // Stop draining on first transient error to avoid hammering.
        if (isNetworkError(err)) break;
      }
    }
  } finally {
    draining = false;
  }
};

/**
 * sendOrQueue — small helper for ProfileContext. Tries the request; if the
 * network is unreachable, queues it and resolves with `{ queued: true }`.
 */
export const sendOrQueue = async ({ method, url, body, headers = {} }) => {
  try {
    const res = await axios({ method, url, data: body, headers });
    return { queued: false, response: res };
  } catch (err) {
    if (isNetworkError(err)) {
      const id = await enqueue({ method, url, body, headers });
      return { queued: true, id };
    }
    throw err;
  }
};

/**
 * Initialise the online-event drain hook. Idempotent — safe to call multiple times.
 */
let listenersWired = false;
export const initSyncListeners = () => {
  if (listenersWired || typeof window === 'undefined') return;
  listenersWired = true;
  window.addEventListener('online', () => { drain(); });
  // Also drain on startup if items are waiting
  if (navigator.onLine) drain();
};
